import React, { useMemo } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import GlassmorphismTokens from '../../styles/GlassmorphismTokens';
import DesignTokens from '../../styles/DesignTokens';
import { SceneBackground, GlassCard, BlueCTAButton } from '../shared/GlassCard';

// --- Radar Chart (pure SVG) ---

const DIMENSIONS = [
  { key: 'culture', label: 'Culture' },
  { key: 'adventure', label: 'Adventure' },
  { key: 'relaxation', label: 'Relaxation' },
  { key: 'food', label: 'Food' },
  { key: 'nature', label: 'Nature' },
  { key: 'social', label: 'Social' },
];

const CHART_SIZE = 220;
const CENTER = CHART_SIZE / 2;
const RADIUS = 85;
const RINGS = 4;

function polarToCartesian(angle, radius) {
  // Start from top (- PI/2)
  const rad = (angle - 90) * (Math.PI / 180);
  return {
    x: CENTER + radius * Math.cos(rad),
    y: CENTER + radius * Math.sin(rad),
  };
}

function RadarChart({ dimensions }) {
  const points = useMemo(() => {
    return DIMENSIONS.map((dim, i) => {
      const angle = (360 / DIMENSIONS.length) * i;
      const value = dimensions[dim.key] || 0;
      return polarToCartesian(angle, RADIUS * value);
    });
  }, [dimensions]);

  const polygonPoints = points.map((p) => `${p.x},${p.y}`).join(' ');

  return (
    <ChartContainer>
      <svg width={CHART_SIZE} height={CHART_SIZE} viewBox={`0 0 ${CHART_SIZE} ${CHART_SIZE}`}>
        {/* Background rings */}
        {Array.from({ length: RINGS }, (_, i) => {
          const r = (RADIUS / RINGS) * (i + 1);
          const ringPoints = DIMENSIONS.map((_, j) => {
            const angle = (360 / DIMENSIONS.length) * j;
            return polarToCartesian(angle, r);
          });
          return (
            <polygon
              key={i}
              points={ringPoints.map((p) => `${p.x},${p.y}`).join(' ')}
              fill="none"
              stroke="rgba(255, 255, 255, 0.1)"
              strokeWidth="1"
            />
          );
        })}

        {/* Axis lines */}
        {DIMENSIONS.map((_, i) => {
          const angle = (360 / DIMENSIONS.length) * i;
          const end = polarToCartesian(angle, RADIUS);
          return (
            <line
              key={i}
              x1={CENTER}
              y1={CENTER}
              x2={end.x}
              y2={end.y}
              stroke="rgba(255, 255, 255, 0.1)"
              strokeWidth="1"
            />
          );
        })}

        {/* Data polygon */}
        <motion.polygon
          points={polygonPoints}
          fill="rgba(66, 133, 244, 0.25)"
          stroke="#4285F4"
          strokeWidth="2"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.6, ease: 'easeOut' }}
          style={{ transformOrigin: `${CENTER}px ${CENTER}px` }}
        />

        {/* Data points */}
        {points.map((p, i) => (
          <motion.circle
            key={i}
            cx={p.x}
            cy={p.y}
            r="4"
            fill="#4285F4"
            stroke="white"
            strokeWidth="1.5"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5 + i * 0.08, duration: 0.3 }}
          />
        ))}

        {/* Labels */}
        {DIMENSIONS.map((dim, i) => {
          const angle = (360 / DIMENSIONS.length) * i;
          const pos = polarToCartesian(angle, RADIUS + 22);
          return (
            <text
              key={dim.key}
              x={pos.x}
              y={pos.y}
              textAnchor="middle"
              dominantBaseline="middle"
              fill="rgba(255, 255, 255, 0.7)"
              fontSize="11"
              fontWeight="500"
            >
              {dim.label}
            </text>
          );
        })}
      </svg>
    </ChartContainer>
  );
}

// --- Styled Components ---

const ResultWrapper = styled.div`
  max-width: 480px;
  width: 100%;
  margin: 0 auto;
  padding: ${DesignTokens.spacing.xl} ${DesignTokens.spacing.lg};
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  justify-content: center;

  @media (max-width: 480px) {
    padding: ${DesignTokens.spacing.lg} ${DesignTokens.spacing.md};
  }
`;

const ResultCard = styled(GlassCard)`
  width: 100%;
  text-align: center;
  padding: ${DesignTokens.spacing['2xl']} ${DesignTokens.spacing.xl};
`;

const PersonalityLabel = styled(motion.div)`
  font-size: ${DesignTokens.typography.fontSize.caption};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  text-transform: uppercase;
  letter-spacing: ${DesignTokens.typography.letterSpacing.wide};
  color: #4285F4;
  margin-bottom: ${DesignTokens.spacing.xs};
`;

const PersonalityTitle = styled(motion.h2)`
  font-size: ${DesignTokens.typography.fontSize.h1};
  font-weight: ${DesignTokens.typography.fontWeight.bold};
  color: white;
  margin: 0 0 ${DesignTokens.spacing.xl} 0;
  line-height: ${DesignTokens.typography.lineHeight.heading};

  @media (max-width: 480px) {
    font-size: ${DesignTokens.typography.fontSize.h2};
  }
`;

const ChartContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${DesignTokens.spacing.xl};
`;

const DimensionBars = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${DesignTokens.spacing.sm};
  margin-bottom: ${DesignTokens.spacing.xl};
`;

const DimensionRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.sm};
`;

const DimensionLabel = styled.span`
  font-size: ${DesignTokens.typography.fontSize.sm};
  color: rgba(255, 255, 255, 0.7);
  width: 72px;
  text-align: right;
  flex-shrink: 0;
`;

const BarTrack = styled.div`
  flex: 1;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: ${DesignTokens.borderRadius.full};
  overflow: hidden;
`;

const BarFill = styled(motion.div)`
  height: 100%;
  background: ${GlassmorphismTokens.gradient.blueCTA};
  border-radius: ${DesignTokens.borderRadius.full};
`;

const DimensionValue = styled.span`
  font-size: ${DesignTokens.typography.fontSize.xs};
  color: rgba(255, 255, 255, 0.5);
  width: 32px;
  flex-shrink: 0;
`;

const BackButton = styled(motion.button)`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: ${DesignTokens.typography.fontSize.sm};
  cursor: pointer;
  margin-top: ${DesignTokens.spacing.md};
  padding: ${DesignTokens.spacing.sm};
  transition: color 0.2s;

  &:hover {
    color: rgba(255, 255, 255, 0.8);
  }
`;

// --- Component ---

const TravelDNAResult = ({ dimensions, personality, onStartPlanning, onBack }) => {
  return (
    <SceneBackground>
      <ResultWrapper>
        <ResultCard
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 200, damping: 20 }}
        >
          <PersonalityLabel
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Your Travel DNA
          </PersonalityLabel>

          <PersonalityTitle
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            {personality}
          </PersonalityTitle>

          <RadarChart dimensions={dimensions} />

          <DimensionBars>
            {DIMENSIONS.map((dim, i) => (
              <DimensionRow key={dim.key}>
                <DimensionLabel>{dim.label}</DimensionLabel>
                <BarTrack>
                  <BarFill
                    initial={{ width: 0 }}
                    animate={{ width: `${(dimensions[dim.key] || 0) * 100}%` }}
                    transition={{ delay: 0.6 + i * 0.1, duration: 0.5, ease: 'easeOut' }}
                  />
                </BarTrack>
                <DimensionValue>{Math.round((dimensions[dim.key] || 0) * 100)}%</DimensionValue>
              </DimensionRow>
            ))}
          </DimensionBars>

          <BlueCTAButton
            onClick={onStartPlanning}
            whileTap={{ scale: 0.97 }}
          >
            <span>Start Planning</span>
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </BlueCTAButton>
        </ResultCard>

        {onBack && (
          <BackButton
            onClick={onBack}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            Back to mood board
          </BackButton>
        )}
      </ResultWrapper>
    </SceneBackground>
  );
};

export default TravelDNAResult;
